/*
  Y0fYDuh8 — ONE source of truth for the customer's selected model.

  Two independent scripts used to write the SAME line-item property onto the
  same `form[action*="/cart/add"]`, and neither read the other:

    studio-model-capture.js  → properties[Model] + properties[Model handle]
                               (from the ?model= slug on the fallback path)
    studio-composer.js       → properties[Model] (from its own radio tiles)

  Two consequences, one visible and one silent:

    • The composer's readiness gate reads only its OWN state, so a customer who
      had already chosen a model was asked to choose AGAIN ("pick a model").
    • Whichever script ran last won the property, so the Model recorded on the
      order could differ from the model the customer actually selected — and
      `properties[Model handle]`, written by the capture script alone, could
      disagree with `properties[Model]` on the same line item. Nothing errors:
      we would render and deliver the wrong model, and the order line would
      "prove" we were right.

  This file is the channel between them. The capture flow PUBLISHES the
  selection; the composer, when present on the page, is the SOLE WRITER of both
  properties and hydrates itself from whatever was published. On pages with no
  composer the capture flow keeps writing the properties itself, exactly as
  before.

  ⚠️ This is a CLASSIC script on purpose, exposing `window.StudioSelectedModel`.
  studio-model-capture.js is loaded `defer` (classic) and studio-composer.js is
  loaded `type="module"`; an ES module could not be imported by the first, and
  duplicating the contract inline on each side is exactly the two-writers shape
  this card exists to remove. One implementation, reachable from both.

  ── OWNERSHIP IS A CLAIM, NOT A MARKER (Stage-1.5 blocking 2) ──────────────
  This used to answer "is [data-studio-composer] in the DOM?". That is ownership
  asserted by something which cannot know whether the owner is ALIVE: the
  composer's entire body — including the add-to-cart gate — sits behind
  `if (composer && getApps().length)`, so whenever the markup renders but the
  module does not run (firebase not initialised, gstatic blocked or slow, any
  throw), the marker said "the composer owns this" while the composer wrote
  nothing and gated nothing. The customer added to cart with NO Model property
  at all — a silent-empty on the one page the capture script exists to serve.

  So the composer now CLAIMS ownership from inside its live block, and
  composerOwnsProperties() reports the claim. No claim ⇒ the capture flow keeps
  writing, exactly as it did before this card. Once claimed, the composer ADOPTS
  any inputs capture already wrote (ensurePropInputs matches by name). Capture
  never withdraws them — a withdraw-on-claim opened a window with zero Model
  inputs if a throw landed between the claim and the composer's own install.
  model-property-owner-check.mjs pins that capture does not subscribe to the
  claim or removeChild.

  ── PRECEDENCE (Stage-1.5 blocking 1) ──────────────────────────────────────
  `source` was recorded and never read. It is read now: a lower-ranked
  publisher may not overwrite a higher-ranked selection, so a capture republish
  — which fires on ANY DOM mutation for 8s after load — can no longer revert a
  model the customer deliberately re-picked in the composer or on
  /pages/my-studio.
*/
(function () {
  var EVENT = 'studio:model-selected';
  var CLAIM_EVENT = 'studio:model-owner-claimed';
  var current = null;
  var owned = false;
  var interacted = false;

  /*
    Publisher precedence. The customer's own click — in the composer or on
    /pages/my-studio's carousel — outranks a selection derived from the
    ?model= URL, which studio-model-capture.js republishes unconditionally
    on every DOM mutation for 8 seconds after load. Equal rank still updates
    (a second pick must win); only a STRICT downgrade is refused.
  */
  var RANK = { composer: 2, 'my-studio': 2, 'model-capture': 1, unknown: 0 };
  function rankOf(source) {
    return Object.prototype.hasOwnProperty.call(RANK, source) ? RANK[source] : 0;
  }

  /**
   * The composer calls this from inside its live block to take ownership of
   * properties[Model] / [Model handle]. Idempotent.
   */
  function claimOwnership() {
    if (owned) return true;
    owned = true;
    document.dispatchEvent(new CustomEvent(CLAIM_EVENT));
    return true;
  }

  /** True only once a live owner has CLAIMED. Never inferred from markup. */
  function composerOwnsProperties() {
    return owned;
  }

  /**
   * Call `fn` when an owner claims — immediately if one already has.
   * Capture must NOT use this to withdraw inputs: the composer adopts
   * existing inputs by name, and withdrawing on claim can leave the form
   * with zero Model properties (enforced by model-property-owner-check.mjs).
   */
  function onOwnershipClaimed(fn) {
    if (owned) { fn(); return function () {}; }
    function handler() { fn(); }
    document.addEventListener(CLAIM_EVENT, handler);
    return function () { document.removeEventListener(CLAIM_EVENT, handler); };
  }

  /**
   * True once the customer has made a selection themselves (a `composer`
   * publish is by definition a click on a tile). Hydration must stop adopting
   * published selections after this, or a late capture republish re-checks the
   * old radio — and a programmatic `radio.checked = true` fires no `change`,
   * so the revert would be terminal.
   */
  function hasInteracted() {
    return interacted;
  }

  /**
   * Record the customer's selection and notify anyone already listening.
   * `handle` is the canonical slug from snippets/model-slug.liquid, or '' when
   * the publishing surface does not know it.
   */
  function publish(name, handle, source) {
    if (!name) return null;
    source = source || 'unknown';

    // PRECEDENCE before idempotence. The old guard compared only against the
    // CURRENT value, so A -> B -> A re-dispatched A: the capture script,
    // holding the URL values frozen at script load, reverted a deliberate
    // re-pick on the next DOM mutation. Refuse the downgrade outright.
    if (current && rankOf(source) < rankOf(current.source)) return current;

    // Idempotent: studio-model-capture.js re-runs on every DOM mutation for 8s
    // (Horizon re-renders sections after first paint), and re-dispatching an
    // unchanged selection would thrash every subscriber.
    if (current && current.name === name && current.handle === (handle || '') && current.source === source) return current;

    current = { name: name, handle: handle || '', source: source };
    // A composer publish IS the customer interacting; latch it so hydration
    // stops adopting anything published afterwards.
    if (source === 'composer') interacted = true;
    document.dispatchEvent(new CustomEvent(EVENT, { detail: current }));
    return current;
  }

  /** The current selection, or null. Safe to call before anything published. */
  function get() {
    return current && current.name ? current : null;
  }

  /**
   * Call `fn` with the selection: immediately if one already exists, and again
   * on every later publish. Returns an unsubscribe function.
   *
   * The immediate call is the whole point — a subscriber that only listens for
   * the event misses a selection published before it loaded, which is precisely
   * the ordering the two scripts could never agree on.
   */
  function onSelected(fn) {
    var existing = get();
    if (existing) fn(existing);
    function handler(e) { fn(e.detail); }
    document.addEventListener(EVENT, handler);
    return function () { document.removeEventListener(EVENT, handler); };
  }

  window.StudioSelectedModel = {
    EVENT: EVENT,
    CLAIM_EVENT: CLAIM_EVENT,
    claimOwnership: claimOwnership,
    composerOwnsProperties: composerOwnsProperties,
    onOwnershipClaimed: onOwnershipClaimed,
    hasInteracted: hasInteracted,
    publish: publish,
    get: get,
    onSelected: onSelected,
  };
})();
